<template>
    <div class="container-fluid">
        <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h5>Stock Report - Department wise</h5>
                <button class="btn btn-primary" @click="downloadExcel">
                    Download Excel
                </button>
            </div>
            <div class="card-body">
                <!-- Department Selection -->
                <div class="row mb-4">
                    <div class="col-md-4">
                        <div class="form-group">
                            <label class="form-label">Select Department</label>
                            <select class="form-select" v-model="selectedDepartment">
                                <option :value="null">Choose Department</option>
                                <option v-for="dept in departments" :key="dept._id" :value="dept">
                                    {{ dept.name }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>                                                                                                                                                                    

                <!-- Stock Table -->
                <div v-if="selectedDepartment" class="table-responsive">
                    <p class="text-muted mb-2">(multiple selections can be done)</p>
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th>Name of the uniform parts</th>
                                <th>Total Quantity</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in filteredStockItems" :key="item.name">
                                <td>{{ item.name }}</td>
                                <td>
                                    <button 
                                        class="btn btn-link"
                                        @click="showDetailedStock(item)"
                                    >
                                        {{ item.quantity }}
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Detailed Stock Modal -->
        <div class="modal fade" :class="{ 'show': showModal }" v-if="showModal">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            Detailed Stock Report - {{ selectedItem?.name }} ({{ selectedDepartment?.name }})
                        </h5>
                        <button type="button" class="btn-close" @click="closeModal"></button>
                    </div>
                    <div class="modal-body">
                        <div class="table-responsive">
                            <table class="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Size</th>
                                        <th>Quantity</th>
                                        <th>Condition</th>
                                        <th>Location</th>
                                        <th>Last Updated</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="detail in selectedItemDetails" :key="detail.size">
                                        <td>{{ detail.size }}</td>
                                        <td>{{ detail.quantity }}</td>
                                        <td>
                                            <span :class="getConditionClass(detail.condition)">
                                                {{ detail.condition }}
                                            </span>
                                        </td>
                                        <td>{{ detail.location }}</td>
                                        <td>{{ formatDate(detail.lastUpdated) }}</td>
                                        <td>
                                            <span :class="getStatusClass(detail.status)">
                                                {{ detail.status }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td><strong>Total</strong></td>
                                        <td colspan="5">
                                            <strong>{{ getTotalQuantity() }}</strong> pieces
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-secondary" @click="closeModal">Close</button>
                        <button class="btn btn-primary" @click="downloadDetailedExcel">
                            Download Detailed Report
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { useToast } from 'vue-toast-notification';
import { fetchGetApi } from '@/util/api';

const toast = useToast();
const showModal = ref(false);
const selectedDepartment = ref<{ _id: string; name: string } | null>(null);
const selectedItem = ref<any>(null);
const selectedItemDetails = ref<any[]>([]);

const departments = ref<Array<{ _id: string; name: string }>>([]);

// Stock data organized by department
const stockData = ref<Array<{
    _id: string;
    itemName: string;
    totalQuantity: number;
    sizes: Array<{
        size: string;
        quantity: number;
        additionalPrice: number;
    }>;
}>>([]);

const baseUrl = process.env.VUE_APP_BASE_URL

// Add function to fetch departments
const fetchDepartments = async () => {
    try {
        const response = await fetchGetApi(`api/department/getAll`);
        departments.value = response.data;
    } catch (error) {
        toast.error('Failed to fetch departments');
        console.error(error);
    }
};

// Add function to fetch products
const fetchProducts = async (departmentId: string) => {
    try {
        const response = await fetchGetApi(`api/product/department/${departmentId}`);
        stockData.value = response.data.data;
    } catch (error) {
        toast.error('Failed to fetch products');
        console.error(error);
    }
};

// Update the computed property:
const filteredStockItems = computed(() => {
    if (!selectedDepartment.value) return [];
    return stockData.value.map(item => ({
        name: item.itemName,
        quantity: item.totalQuantity,
        _id: item._id,
        sizes: item.sizes
    }));
});

// Add watch for selectedDepartment:
watch(selectedDepartment, async (newDept) => {
    if (newDept?._id) {
        await fetchProducts(newDept._id);
    } else {
        stockData.value = [];
    }
});

// Add onMounted hook:
onMounted(async () => {
    await fetchDepartments();
});

const showDetailedStock = async (item: any) => {
    selectedItem.value = item;
    // Mock detailed data - replace with actual API call
    selectedItemDetails.value = [
        { 
            size: 'S', 
            quantity: 10, 
            condition: 'New', 
            location: 'Main Store',
            status: 'In Stock',
            lastUpdated: new Date() 
        },
        { 
            size: 'M', 
            quantity: 15, 
            condition: 'Used', 
            location: 'Main Store',
            status: 'In Stock',
            lastUpdated: new Date() 
        },
        { 
            size: 'L', 
            quantity: 20, 
            condition: 'New', 
            location: 'Site Store',
            status: 'Reserved',
            lastUpdated: new Date() 
        },
        { 
            size: 'XL', 
            quantity: 5, 
            condition: 'Damaged', 
            location: 'Main Store',
            status: 'In Laundry',
            lastUpdated: new Date() 
        }
    ];
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    selectedItem.value = null;
};

const formatDate = (date: Date) => {
    return format(date, 'dd-MM-yyyy HH:mm');
};

const getConditionClass = (condition: string) => {
    const classes = {
        'New': 'badge bg-success',
        'Used': 'badge bg-warning',
        'Damaged': 'badge bg-danger'
    };
    return classes[condition as keyof typeof classes] || 'badge bg-secondary';
};

const getStatusClass = (status: string) => {
    const classes = {
        'In Stock': 'badge bg-success',
        'Reserved': 'badge bg-warning',
        'In Laundry': 'badge bg-info'
    };
    return classes[status as keyof typeof classes] || 'badge bg-secondary';
};

const getTotalQuantity = () => {
    return selectedItemDetails.value.reduce((total, item) => total + item.quantity, 0);
};

const downloadExcel = () => {
    if (!selectedDepartment.value) {
        toast.error('Please select a department first');
        return;
    }

    const worksheet = XLSX.utils.json_to_sheet(filteredStockItems.value);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `${selectedDepartment.value.name} Stock`);
    XLSX.writeFile(workbook, `${selectedDepartment.value.name}_stock_report.xlsx`);
};

const downloadDetailedExcel = () => {
    if (!selectedItem.value) return;

    const worksheet = XLSX.utils.json_to_sheet(selectedItemDetails.value);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `${selectedItem.value.name} Details`);
    XLSX.writeFile(workbook, `${selectedItem.value.name}_detailed_stock.xlsx`);
};
</script>

<style scoped>
.table th {
    background-color: #f8f9fa;
    font-weight: bold;
}

.table td, .table th {
    vertical-align: middle;
    padding: 0.75rem;
}

.text-muted {
    font-style: italic;
}

.btn-link {
    text-decoration: none;
    color: #007bff;
    padding: 0;
}

.btn-link:hover {
    text-decoration: underline;
    color: #0056b3;
}

.modal.show {
    display: block;
    background-color: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
    margin: 1.75rem auto;
}

.modal-lg {
    max-width: 800px;
}

.badge {
    font-size: 0.875em;
    padding: 0.35em 0.65em;
}

@media print {
    .modal-dialog {
        max-width: 100%;
        margin: 0;
    }

    .modal-footer {
        display: none;
    }

    .btn-close {
        display: none;
    }
}
</style>